import { useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { buildChatSrc } from '@core/bridge/contracts/chatContract'
import { musicManager } from '@core/audio/music/musicManager'
import { soundManager } from '@core/audio/sounds/soundManager'
import { useChatBridge } from './useChatBridge'

/**
 * The external chat iframe. Game-agnostic and layout-agnostic: it only knows how
 * to preload the chat and reveal/hide itself.
 *
 * Mount-once semantics — it renders `null` until the session identity is ready
 * (mirrors the FE's init gate), and once the `src` is set it stays mounted and
 * preloads in the background. Hidden state uses `display:none` (never unmount),
 * so the chat socket stays alive and opening is instant.
 *
 *  - `docked`  → desktop: fills its activity-panel container.
 *  - !docked   → mobile/tablet: full-screen overlay on top of the game.
 *  - `visible` → whether it's currently shown.
 */
const ChatFrame = ({ docked = false, visible = false }) => {
  const iframeRef = useRef(null)

  const details = useSelector((state) => state.session.details)
  const currency = useSelector((state) => state.wallet.currencyCode)
  const musicEnabled = useSelector((state) => state.ui.audio.music.enabled)
  const soundsEnabled = useSelector((state) => state.ui.audio.sounds.enabled)

  const src = useMemo(() => buildChatSrc({ ...details, currency }), [details, currency])

  // Desktop docked chat shows no ✕ (closed via tab switch); the mobile overlay
  // shows the child's ✕ (which notifies us to close).
  useChatBridge({ iframeRef, showCloseIcon: !docked, enabled: Boolean(src) })

  // Mobile only: duck the game audio while the chat overlay is on top, and
  // restore the player's prior music/SFX prefs once on close. Ref-guarded so a
  // stray re-render can't double-duck or double-restore.
  const priorAudioRef = useRef(null)
  const restoreAudio = () => {
    if (!priorAudioRef.current) return
    musicManager.setEnabled(priorAudioRef.current.music)
    soundManager.setEnabled(priorAudioRef.current.sounds)
    priorAudioRef.current = null
  }

  useEffect(() => {
    if (docked) return undefined
    if (visible && !priorAudioRef.current) {
      priorAudioRef.current = { music: musicEnabled, sounds: soundsEnabled }
      // Gain change, never a transport change — pausing the music here would
      // suspend the shared AudioContext and kill all audio on iOS.
      musicManager.setEnabled(false)
      soundManager.setEnabled(false)
    } else if (!visible) {
      restoreAudio()
    }
    return undefined
    // Prior prefs are captured at open; re-running on toggle changes is unwanted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, docked])

  // Safety net: restore audio if the overlay unmounts while still open.
  useEffect(() => () => restoreAudio(), [])

  if (!src) return null

  const containerClassName = docked
    ? `chat-frame-container chat-frame-docked ${visible ? 'chat-frame-docked-visible' : 'chat-frame-docked-hidden'}`
    : `chat-frame-container chat-frame-overlay ${visible ? 'chat-frame-overlay-visible' : 'chat-frame-overlay-hidden'}`

  return (
    <div className={containerClassName}>
      <iframe
        ref={iframeRef}
        title="games-chat"
        className="chat-frame-iframe"
        src={src}
        allow="clipboard-write"
      />
    </div>
  )
}

export default ChatFrame
